import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect } from "react";
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $isElementNode,
  COMMAND_PRIORITY_LOW,
  LexicalNode,
  TextNode,
  ElementNode
} from "lexical";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isListItemNode, REMOVE_LIST_COMMAND } from "@lexical/list";

import { CLEAR_FORMATTING_COMMAND } from "../commands/clearFormatting";
import { ClearFormattingPluginProps } from "../types";

const collectTextNodes = (node: LexicalNode, textNodes: TextNode[]): void => {
  if ($isTextNode(node)) {
    textNodes.push(node);
    return;
  }

  if ($isElementNode(node)) {
    const children = (node as ElementNode).getChildren();
    if (children.length > 0) {
      children.forEach((child) => collectTextNodes(child, textNodes));
    }
  }
};

const hasLinkInSelection = (nodes: LexicalNode[]): boolean => {
  return nodes.some((node) => {
    if ($isLinkNode(node)) return true;
    const parent = node.getParent();
    return parent !== null && $isLinkNode(parent);
  });
};

const hasListItemInSelection = (nodes: LexicalNode[]): boolean => {
  return nodes.some((node) => {
    if ($isListItemNode(node)) return true;
    const parent = node.getParent();
    return parent !== null && $isListItemNode(parent);
  });
};

const clearTextFormatting = (textNodes: TextNode[]): void => {
  textNodes.forEach((node) => {
    // Format value of 0 removes all formatting flags
    node.setFormat(0);
  });
};

// Removes all formatting from selected text
export const ClearFormattingPlugin = ({
  disabled = false
}: ClearFormattingPluginProps): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (disabled) return;

    return editor.registerCommand(
      CLEAR_FORMATTING_COMMAND,
      () => {
        // Validate selection first before any operations
        let isValidSelection = false;
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          isValidSelection = $isRangeSelection(selection);
        });

        if (!isValidSelection) return true;

        editor.update(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) return;

          const nodes = selection.getNodes();
          if (nodes.length === 0) return;

          const textNodes: TextNode[] = [];
          nodes.forEach((node) => collectTextNodes(node, textNodes));
          if (textNodes.length > 0) {
            clearTextFormatting(textNodes);
          }
        });

        // Handle links and lists in a separate read cycle
        editor.getEditorState().read(() => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) return;

          const nodes = selection.getNodes();
          if (nodes.length === 0) return;

          if (hasLinkInSelection(nodes)) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
          }
          if (hasListItemInSelection(nodes)) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          }
        });

        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, disabled]);

  return null;
};
